import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import EventoService from '../../services/eventoService';
import NotificacionService from '../../services/notificacionService';
import CrearEventoModal from '../../components/CrearEventoModal';
import ChecklistTramite from '../../components/Tramite/ChecklistTramite';
import TarjetaCobroTramite from '../../components/Cobranzas/TarjetaCobroTramite';
import BotonCancelarReactivarTramite from '../../components/Tramite/BotonCancelarReactivarTramite';
import SeccionCobranzaTramite from '../../components/Cobranzas/SeccionCobranzaTramite';
import HistorialTramite from '../../components/HistorialTramite';
import PanelAccionesAvanzadasTramite from '../../components/Administracion/PanelAccionesAvanzadasTramite';
import BannerEstadoFinal from '../../components/BannerEstadoFinal';
import WhatsAppHeaderButton from '../../components/WhatsAppHeaderButton';
import PanelUbicacionTramite from '../../components/PanelUbicacionTramite';
import FeedbackButton from '../../components/FeedbackButton';
import NotaComentario from '../../components/NotaComentario';
import TablaHerederos from '../../components/Declaratoria/TablaHerederos';
import BotonGenerarCaratula from '../../components/Caratula/BotonGenerarCaratula';
import ComboboxDatosMaestros from '../../components/ComboboxDatosMaestros';
import DatosMaestrosService from '../../services/datosMaestrosService';
import useCustomModal from '../../hooks/useCustomModal';
import useVolverAtras from '../../hooks/useVolverAtras';
import CustomModal from '../../components/CustomModal';
import { useAuth } from '../../auth/AuthContext';
import {
    getTramiteDeclaratoriaById, actualizarTramiteDeclaratoria, actualizarSeguimientoDeclaratoria,
    actualizarComentariosDeclaratoria, avanzarEstadoDeclaratoria, actualizarChecklistDeclaratoria,
} from '../../services/declaratoriaService';
import { subirArchivo, descargarArchivo, obtenerArchivosPorTramite, eliminarArchivo } from '../../services/archivoService';
import { apellidoNombre } from '../../utils/nombrePersona';
import { esDniOCuilValido, mensajeDniOCuilInvalido } from '../../utils/documentoUtils';
import EtiquetaTramite from '../../components/EtiquetaTramite';

const formatearFechaEvento = (fechaString) => {
    if (!fechaString) return '';
    const [y, m, d] = fechaString.split('T')[0].split('-');
    return `${d}/${m}/${y}`;
};

// Mini-formulario por transición (patrón Ganancias/Minusvalía). CERRADO_DESFAVORABLE está
// disponible desde cualquier estado posterior a la presentación (Iniciado en OPS en adelante).
const TRANSICION_DESFAVORABLE = {
    destino: 'CERRADO_DESFAVORABLE',
    label: 'Cerrar desfavorable',
    icon: 'bi-x-octagon',
    variant: 'outline-secondary',
    campos: [
        { name: 'motivoDesfavorable', label: 'Motivo (por qué no prosperó)', type: 'textarea' },
    ],
};

const TRANSICION_CANCELAR = {
    destino: 'CANCELADO',
    label: 'Cancelar trámite',
    icon: 'bi-slash-circle-fill',
    variant: 'outline-secondary',
    campos: [
        { name: 'comentario', label: 'Motivo de la cancelación', type: 'textarea' },
    ],
};

const TRANSICION_REACTIVAR = {
    destino: 'PREPARACION_DEMANDA',
    label: 'Reactivar trámite',
    icon: 'bi-arrow-counterclockwise',
    variant: 'outline-primary',
    campos: [
        { name: 'comentario', label: 'Motivo de la reactivación', type: 'textarea' },
    ],
};

const TRANSICIONES_POR_ESTADO = {
    'Preparación de demanda': [
        {
            destino: 'INICIADO_OPS', label: 'Iniciar en OPS', icon: 'bi-send-check', variant: 'primary',
            campos: [
                { name: 'fechaInicioOps', label: 'Fecha de presentación', type: 'date' },
                { name: 'nroExpedienteSac', label: 'N° de expediente SAC', type: 'text' },
                { name: 'caratulaJudicial', label: 'Carátula judicial (opcional, ya sugerida)', type: 'text' },
            ],
        },
        TRANSICION_CANCELAR,
    ],
    'Iniciado en OPS': [
        {
            destino: 'DECRETO_INICIAL', label: 'Registrar decreto inicial', icon: 'bi-file-earmark-check', variant: 'primary',
            campos: [
                { name: 'fechaDecretoInicial', label: 'Fecha del decreto', type: 'date' },
                { name: 'juzgado', label: 'Juzgado', type: 'lugar', tipoLugar: 'Juzgado' },
            ],
        },
        TRANSICION_DESFAVORABLE,
        TRANSICION_CANCELAR,
    ],
    'Decreto inicial': [
        {
            destino: 'PUBLICACION_BOE', label: 'Registrar publicación en BOE', icon: 'bi-newspaper', variant: 'primary',
            campos: [
                { name: 'fechaPublicacionBoe', label: 'Fecha de envío al Boletín Oficial', type: 'date' },
            ],
        },
        TRANSICION_DESFAVORABLE,
        TRANSICION_CANCELAR,
    ],
    'Publicación en Boletín Oficial': [
        {
            destino: 'FIJACION_AUDIENCIA', label: 'Fijar audiencia', icon: 'bi-calendar-event', variant: 'primary',
            campos: [
                { name: 'fechaAudiencia', label: 'Fecha de la audiencia', type: 'date' },
                { name: 'horaAudiencia', label: 'Hora de la audiencia', type: 'time' },
            ],
        },
        TRANSICION_DESFAVORABLE,
        TRANSICION_CANCELAR,
    ],
    'Fijación de audiencia': [
        {
            destino: 'CLAUSURA_AUDIENCIA', label: 'Registrar clausura', icon: 'bi-file-earmark-check', variant: 'primary',
            campos: [
                { name: 'fechaClausura', label: 'Fecha del escrito que pide sentencia', type: 'date' },
                { name: 'nroUltimoEscrito', label: 'N° de escrito (opcional)', type: 'text' },
            ],
        },
        TRANSICION_DESFAVORABLE,
        TRANSICION_CANCELAR,
    ],
    'Clausura de audiencia': [
        {
            destino: 'CERRADO_FAVORABLE', label: 'Cerrar favorable (sentencia)', icon: 'bi-check-circle-fill', variant: 'success',
            campos: [
                { name: 'fechaSentencia', label: 'Fecha de la sentencia', type: 'date' },
            ],
        },
        TRANSICION_DESFAVORABLE,
        TRANSICION_CANCELAR,
    ],
    'Cancelado': [TRANSICION_REACTIVAR],
};

function MiniFormularioTransicion({ idTramite, transicion, onGuardado }) {
    const [abierto, setAbierto] = useState(false);
    const [valores, setValores] = useState({});
    const [guardando, setGuardando] = useState(false);
    const [lugares, setLugares] = useState([]);

    const tieneCampoLugar = transicion.campos.some(c => c.type === 'lugar');

    // Carga el catálogo de Lugares solo si esta transición tiene un campo conectado a él
    // (ej. "Juzgado" en Decreto inicial), mismo mecanismo que la sede de Junta Médica en
    // Minusvalía: ComboboxDatosMaestros sugiere los que ya existen y crea uno nuevo al vuelo.
    useEffect(() => {
        if (!abierto || !tieneCampoLugar) return;
        DatosMaestrosService.getLugares()
            .then((resp) => setLugares(resp.data || []))
            .catch((err) => console.error('Error al cargar lugares:', err));
    }, [abierto, tieneCampoLugar]);

    const handleChange = (name, value) => setValores(prev => ({ ...prev, [name]: value }));

    // Si el operador tipeó un lugar nuevo y confirmó sin que el combobox llegara a crearlo
    // (onBlur no llegó a dispararse), lo crea acá antes de avanzar de estado — mismo respaldo
    // que TramiteMinusvaliaService/FormularioJuntaMedica.crearLugarSiNoExiste.
    const crearLugaresFaltantes = async () => {
        for (const campo of transicion.campos) {
            if (campo.type !== 'lugar') continue;
            const nombreTrim = (valores[campo.name] || '').trim();
            if (!nombreTrim) continue;
            const yaExiste = lugares.some(l => (l.nombre || '').trim().toLowerCase() === nombreTrim.toLowerCase());
            if (yaExiste) continue;
            try {
                await DatosMaestrosService.createLugar({
                    nombre: nombreTrim,
                    tipo: campo.tipoLugar || 'Oficina',
                    direccion: '',
                    telefono: '',
                    descripcion: 'Creado automáticamente desde trámite de Declaratoria',
                });
            } catch (err) {
                console.error('Error al crear lugar automáticamente:', err);
            }
        }
    };

    const guardar = async () => {
        setGuardando(true);
        try {
            if (tieneCampoLugar) {
                await crearLugaresFaltantes();
            }
            const payload = { destino: transicion.destino, ...valores };
            const actualizado = await avanzarEstadoDeclaratoria(idTramite, payload);
            onGuardado(actualizado);
            toast.success('Estado actualizado correctamente');
            setAbierto(false);
            setValores({});
        } catch (err) {
            console.error('Error al avanzar de estado:', err);
            toast.error(err.response?.data || 'No se pudo avanzar de estado');
        } finally {
            setGuardando(false);
        }
    };

    if (!abierto) {
        return (
            <button className={`btn btn-${transicion.variant || 'primary'} d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold`} onClick={() => setAbierto(true)}>
                <i className={`bi ${transicion.icon || 'bi-arrow-right-circle'}`}></i>{transicion.label}
            </button>
        );
    }

    return (
        <div className="p-4 border rounded-3 bg-light w-100">
            <h5 className="fw-semibold mb-3" style={{ color: '#9f1239' }}>
                <i className={`bi ${transicion.icon || 'bi-arrow-right-circle'} me-2`}></i>{transicion.label}
            </h5>
            <div className="row g-3">
                {transicion.campos.map(campo => (
                    <div key={campo.name} className={campo.type === 'textarea' ? 'col-12' : 'col-md-6'}>
                        <label className="form-label fw-semibold">{campo.label}</label>
                        {campo.type === 'textarea' ? (
                            <textarea className="form-control shadow-sm" rows={3}
                                value={valores[campo.name] || ''} onChange={(e) => handleChange(campo.name, e.target.value)} />
                        ) : campo.type === 'lugar' ? (
                            <ComboboxDatosMaestros
                                opciones={lugares.filter(l => l.tipo === campo.tipoLugar || !l.tipo)}
                                valor={valores[campo.name] || ''}
                                onChange={(valor) => handleChange(campo.name, valor)}
                                tipo="lugar"
                                tipoLugar={campo.tipoLugar}
                                placeholder="Seleccione o escriba..."
                            />
                        ) : (
                            <input type={campo.type} className="form-control shadow-sm"
                                value={valores[campo.name] || ''} onChange={(e) => handleChange(campo.name, e.target.value)} />
                        )}
                    </div>
                ))}
            </div>
            <div className="d-flex justify-content-center gap-3 mt-4">
                <button className="btn btn-primary d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold" onClick={guardar} disabled={guardando}
                    style={{ background: 'var(--primary)', border: 'none' }}>
                    {guardando ? <span className="spinner-border spinner-border-sm" role="status"></span> : <><i className="bi bi-check-circle-fill"></i>Confirmar</>}
                </button>
                <button className="btn btn-outline-secondary d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                    onClick={() => { setAbierto(false); setValores({}); }} disabled={guardando}>
                    Cancelar
                </button>
            </div>
        </div>
    );
}

function SeccionAvanzarEstado({ idTramite, nombreEstadoActual, onAvanzado }) {
    const transiciones = TRANSICIONES_POR_ESTADO[nombreEstadoActual] || [];
    if (transiciones.length === 0) {
        return (
            <div className="alert alert-secondary border-0 rounded-3 mb-0 d-flex align-items-center gap-2">
                <i className="bi bi-flag-fill"></i>Este trámite está en una etapa final del ciclo de estados.
            </div>
        );
    }
    return (
        <div className="d-flex flex-wrap gap-3">
            {transiciones.map(t => (
                <MiniFormularioTransicion key={t.destino} idTramite={idTramite} transicion={t} onGuardado={onAvanzado} />
            ))}
        </div>
    );
}

function VistaDetalladaTramiteDeclaratoria() {
    const { idTramite } = useParams();
    const navigate = useNavigate();
    const volverAtras = useVolverAtras('/tramites');
    const { user } = useAuth();
    const { modalState, hideModal, showModal } = useCustomModal();

    const [tramite, setTramite] = useState(null);
    const [loading, setLoading] = useState(true);

    const [editMode, setEditMode] = useState(false);
    const [tramiteEdit, setTramiteEdit] = useState(null);
    const [guardandoDatos, setGuardandoDatos] = useState(false);

    const [editSeguimiento, setEditSeguimiento] = useState(false);
    const [seguimientoEdit, setSeguimientoEdit] = useState(null);
    const [guardandoSeguimiento, setGuardandoSeguimiento] = useState(false);

    const [eventos, setEventos] = useState([]);
    const [cargandoEventos, setCargandoEventos] = useState(true);
    const [mostrarModalEvento, setMostrarModalEvento] = useState(false);
    const [fechaSeleccionadaEvento, setFechaSeleccionadaEvento] = useState(new Date());

    const [modalHistorialAbierto, setModalHistorialAbierto] = useState(false);

    const [archivos, setArchivos] = useState([]);
    const [subiendoGeneral, setSubiendoGeneral] = useState(false);
    const [subiendoItem, setSubiendoItem] = useState(null);

    const recargarTramite = useCallback(async () => {
        try {
            const data = await getTramiteDeclaratoriaById(idTramite);
            setTramite(data);
        } catch (error) {
            console.error('Error al cargar trámite de Declaratoria:', error);
        }
    }, [idTramite]);

    const recargarArchivos = useCallback(async () => {
        try {
            const data = await obtenerArchivosPorTramite(idTramite);
            setArchivos(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error al cargar archivos:', error);
        }
    }, [idTramite]);

    const recargarEventos = useCallback(async () => {
        try {
            const response = await EventoService.getEventosPorTramite(idTramite);
            setEventos(response.data || []);
        } catch (error) {
            console.error('Error recargando eventos:', error);
        }
    }, [idTramite]);

    useEffect(() => {
        const cargar = async () => {
            setLoading(true);
            setCargandoEventos(true);
            try {
                const [tramiteResult, eventosResult, archivosResult] = await Promise.allSettled([
                    getTramiteDeclaratoriaById(idTramite),
                    EventoService.getEventosPorTramite(idTramite),
                    obtenerArchivosPorTramite(idTramite),
                ]);
                setTramite(tramiteResult.status === 'fulfilled' ? tramiteResult.value : null);
                setEventos(eventosResult.status === 'fulfilled' ? (eventosResult.value.data || []) : []);
                setArchivos(archivosResult.status === 'fulfilled' && Array.isArray(archivosResult.value) ? archivosResult.value : []);
            } finally {
                setLoading(false);
                setCargandoEventos(false);
            }
        };
        cargar();
    }, [idTramite]);

    useEffect(() => {
        const handleEventoCreado = () => recargarEventos();
        window.addEventListener('eventoCreadoDesdeChat', handleEventoCreado);
        const interval = setInterval(() => recargarEventos(), 30000);
        return () => {
            window.removeEventListener('eventoCreadoDesdeChat', handleEventoCreado);
            clearInterval(interval);
        };
    }, [recargarEventos]);

    const formatLocalFecha = (fechaInput) => {
        if (!fechaInput) return '';
        if (typeof fechaInput === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(fechaInput)) return fechaInput;
        const d = new Date(fechaInput);
        if (Number.isNaN(d.getTime())) return '';
        return new Date(d.getTime() - (d.getTimezoneOffset() * 60000)).toISOString().split('T')[0];
    };

    const handleGuardarEvento = async (evento) => {
        try {
            const responseEvento = await EventoService.crearEvento({
                ...evento,
                fecha: formatLocalFecha(evento.fecha),
                idTramite: Number(idTramite),
            });
            const idEventoCreado = responseEvento.data.idEvento || responseEvento.data;
            if (evento.tiemposRecordatorio && evento.tiemposRecordatorio.length > 0) {
                await NotificacionService.programarNotificacionesAutomaticas(idEventoCreado, evento.tiemposRecordatorio);
            }
            window.dispatchEvent(new CustomEvent('eventoCreadoDesdeChat', { detail: { timestamp: Date.now(), source: 'tramite-declaratoria' } }));
            await recargarEventos();
            setMostrarModalEvento(false);
            toast.success('Evento creado correctamente');
        } catch (error) {
            console.error('Error al crear evento:', error);
            toast.error('Error al crear el evento');
        }
    };

    const descargarPorId = async (idArchivo) => {
        try {
            const meta = archivos.find(a => a.idArchivo === idArchivo);
            const nombre = meta?.nombre || `archivo_${idArchivo}`;
            const resp = await descargarArchivo(idArchivo);
            const blob = meta?.tipo ? new Blob([resp.data], { type: meta.tipo }) : new Blob([resp.data]);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = nombre;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error al descargar archivo:', err);
            toast.error('No se pudo descargar el archivo');
        }
    };

    const subirGeneral = async (file) => {
        if (!file) return;
        setSubiendoGeneral(true);
        try {
            await subirArchivo(idTramite, file);
            await recargarArchivos();
            toast.success('Archivo subido');
        } catch (err) {
            console.error('Error al subir archivo:', err);
            toast.error('No se pudo subir el archivo');
        } finally {
            setSubiendoGeneral(false);
        }
    };
    const eliminarGeneral = (idArchivo) => {
        showModal({
            title: 'Eliminar archivo',
            type: 'warning',
            showCancel: true,
            confirmText: 'Sí, eliminar',
            cancelText: 'Cancelar',
            confirmVariant: 'warning',
            message: '¿Seguro que querés eliminar este archivo? Esta acción no se puede deshacer.',
            onConfirm: async () => {
                try {
                    await eliminarArchivo(idArchivo);
                    await recargarArchivos();
                    toast.info('Archivo eliminado');
                } catch (err) {
                    console.error('Error al eliminar archivo:', err);
                    toast.error('No se pudo eliminar el archivo');
                }
            },
        });
    };

    // ---- Checklist (formulario_catalogo) ----
    const togglePresentado = async (item) => {
        if (item.idArchivo) return;
        try {
            await actualizarChecklistDeclaratoria(item.id, !item.presentado, null);
            recargarTramite();
        } catch (err) {
            console.error('Error al actualizar checklist:', err);
        }
    };
    const adjuntarArchivoChecklist = async (item, file) => {
        if (!file) return;
        setSubiendoItem(item.id);
        try {
            const resp = await subirArchivo(idTramite, file);
            const idArchivo = resp?.idArchivo ?? null;
            await actualizarChecklistDeclaratoria(item.id, true, idArchivo);
            await Promise.all([recargarTramite(), recargarArchivos()]);
        } catch (err) {
            console.error('Error al adjuntar archivo:', err);
            toast.error('No se pudo adjuntar el archivo');
        } finally {
            setSubiendoItem(null);
        }
    };
    const quitarAdjuntoChecklist = (item) => {
        showModal({
            title: 'Eliminar documento',
            type: 'warning',
            showCancel: true,
            confirmText: 'Sí, eliminar',
            cancelText: 'Cancelar',
            confirmVariant: 'warning',
            message: `¿Seguro que querés eliminar el archivo adjunto de "${item.nombre}"?`,
            onConfirm: async () => {
                try {
                    await eliminarArchivo(item.idArchivo);
                    await actualizarChecklistDeclaratoria(item.id, false, null);
                    await Promise.all([recargarTramite(), recargarArchivos()]);
                    toast.info('Documento eliminado');
                } catch (err) {
                    console.error('Error al eliminar documento:', err);
                    toast.error('No se pudo eliminar el documento');
                }
            },
        });
    };

    const handleGuardarComentario = async (nuevoComentario) => {
        try {
            await actualizarComentariosDeclaratoria(idTramite, nuevoComentario);
            await recargarTramite();
            toast.success('Comentario actualizado correctamente');
        } catch (error) {
            console.error('Error al guardar comentario:', error);
            toast.error('No se pudo guardar el comentario');
            throw error;
        }
    };

    const handleModificar = () => {
        setTramiteEdit({
            causanteNombre: tramite.causanteNombre || '',
            causanteDniCuil: tramite.causanteDniCuil || '',
            causanteFechaFallecimiento: tramite.causanteFechaFallecimiento || '',
            causantePadre: tramite.causantePadre || '',
            causanteMadre: tramite.causanteMadre || '',
            causante2Nombre: tramite.causante2Nombre || '',
            causante2DniCuil: tramite.causante2DniCuil || '',
            causante2FechaFallecimiento: tramite.causante2FechaFallecimiento || '',
            etiqueta: tramite.etiqueta || '',
        });
        setEditMode(true);
    };
    const handleGuardarDatos = async () => {
        if (!esDniOCuilValido(tramiteEdit.causanteDniCuil)) {
            toast.error(mensajeDniOCuilInvalido('El DNI/CUIL del causante'));
            return;
        }
        setGuardandoDatos(true);
        try {
            await actualizarTramiteDeclaratoria(idTramite, tramiteEdit);
            await recargarTramite();
            setEditMode(false);
            toast.success('Datos del trámite actualizados');
        } catch (error) {
            console.error('Error al guardar datos:', error);
            toast.error(error.response?.data || 'No se pudieron guardar los datos');
        } finally {
            setGuardandoDatos(false);
        }
    };

    const handleModificarSeguimiento = () => {
        setSeguimientoEdit({
            caratulaJudicial: tramite.caratulaJudicial || '',
            nroExpedienteSac: tramite.nroExpedienteSac || '',
            juzgado: tramite.juzgado || '',
            nroUltimoEscrito: tramite.nroUltimoEscrito || '',
            fechaUltimoEscrito: tramite.fechaUltimoEscrito || '',
            observacionTribunal: tramite.observacionTribunal || '',
            fechaPublicacionBoe: tramite.fechaPublicacionBoe || '',
            fechaAudiencia: tramite.fechaAudiencia || '',
            horaAudiencia: tramite.horaAudiencia || '',
        });
        setEditSeguimiento(true);
    };
    const handleGuardarSeguimiento = async () => {
        setGuardandoSeguimiento(true);
        try {
            await actualizarSeguimientoDeclaratoria(idTramite, seguimientoEdit);
            await recargarTramite();
            await recargarEventos();
            setEditSeguimiento(false);
            toast.success('Seguimiento actualizado');
        } catch (error) {
            console.error('Error al guardar el seguimiento:', error);
            toast.error(error.response?.data || 'No se pudo guardar el seguimiento');
        } finally {
            setGuardandoSeguimiento(false);
        }
    };

    const marcarObservacionResuelta = async () => {
        try {
            await actualizarSeguimientoDeclaratoria(idTramite, { observacionTribunalResuelta: true });
            await recargarTramite();
            toast.success('Observación marcada como resuelta');
        } catch (error) {
            console.error('Error al marcar la observación como resuelta:', error);
            toast.error('No se pudo actualizar la observación');
        }
    };

    const handleAvanzarEstado = (tramiteActualizado) => setTramite(tramiteActualizado);

    const verDetalleCliente = (cuil) => navigate(`/clientes/vista-detallada/${cuil.toString()}`);

    if (loading) {
        return (
            <div className="text-center my-5">
                <div className="spinner-border" style={{ width: '3rem', height: '3rem', color: '#9f1239' }}></div>
                <p className="mt-3 fw-semibold" style={{ color: '#6B7280' }}>Cargando trámite...</p>
            </div>
        );
    }
    if (!tramite) {
        return <div className="alert alert-danger mt-4">No se encontró el trámite.</div>;
    }

    const cliente = tramite.cliente || {};
    const formularios = tramite.formularios || [];
    const idsEnChecklist = new Set(formularios.map(f => f.idArchivo).filter(Boolean));
    const archivosOtros = archivos.filter(a => !idsEnChecklist.has(a.idArchivo));
    const nombrePorId = archivos.reduce((acc, a) => { acc[a.idArchivo] = a.nombre; return acc; }, {});
    const formatTamano = (bytes) => (bytes == null ? '' : (bytes / (1024 * 1024)).toFixed(2) + ' MB');

    const diasRestantes = tramite.diasRestantesBoe;
    const colorPlazo = diasRestantes == null ? null : (diasRestantes < 0 ? 'danger' : diasRestantes <= 7 ? 'warning' : 'success');

    return (
        <div className="container mt-4">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-start mb-4">
                <div style={{ flex: '1 1 20rem', minWidth: 0 }}>
                    <div className="d-flex align-items-center flex-wrap gap-3 mb-1">
                        <h2 className="fw-bold mb-0" style={{ color: '#9f1239' }}>Detalles del Trámite de Declaratoria de Herederos</h2>
                        <span className="badge rounded-pill fs-6 px-3" style={{ backgroundColor: '#ffe4e6', color: '#9f1239', border: '1px solid #fecdd3' }}>
                            DECLARATORIA
                        </span>
                        <EtiquetaTramite etiqueta={tramite.etiqueta} />
                    </div>
                    {cliente?.apellido && cliente?.nombre && (
                        <div className="d-flex align-items-center gap-2 flex-wrap mt-2">
                            <h5 className="text-secondary fw-semibold mb-0">
                                <PersonSearchIcon fontSize="small" className="me-1 mb-1" />
                                {apellidoNombre(cliente.apellido, cliente.nombre)}
                            </h5>
                            {cliente.telefonoPropio && (
                                <WhatsAppHeaderButton
                                    phone={cliente.telefonoPropio}
                                    clientName={`${cliente.nombre} ${cliente.apellido}`}
                                    onClick={() => window.dispatchEvent(new CustomEvent('openAdminSidebar', { detail: { tab: 'whatsapp' } }))}
                                />
                            )}
                        </div>
                    )}
                    <PanelUbicacionTramite tramite={tramite} onUpdated={setTramite} />
                </div>
                <div className="d-flex gap-2 mt-1 align-items-center flex-wrap">
                    {/* Recarga el trámite además de los archivos: esta carátula edita las
                        columnas de preparación (fecha de solicitud de actas, fotos de DNI,
                        fecha de firma) y el teléfono del cliente. */}
                    <BotonGenerarCaratula
                        tipo="DECLARATORIA_HEREDEROS"
                        idTramite={tramite.idTramite}
                        onGenerado={() => Promise.all([recargarTramite(), recargarArchivos()])}
                    />
                    <button className="btn btn-outline-secondary d-flex align-items-center gap-2 rounded-pill" onClick={volverAtras}>
                        <ArrowBackIcon fontSize="small" /> Volver
                    </button>
                </div>
            </div>

            <BannerEstadoFinal idTramite={tramite.idTramite} nombreEstadoActual={tramite.nombreEstadoActual} />

            {/* Estado del ciclo */}
            <div className="card shadow-sm border-0 rounded-4 p-4 mb-3" style={{ backgroundColor: '#f8f9fa' }}>
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                    <div className="d-flex align-items-center gap-3">
                        <h5 className="fw-bold text-secondary mb-0">Estado</h5>
                        <span className="badge rounded-pill fs-6 px-3" style={{ backgroundColor: '#ffe4e6', color: '#9f1239', border: '1px solid #fecdd3' }}>
                            {tramite.nombreEstadoActual || 'Preparación de demanda'}
                        </span>
                        {tramite.fechaEstadoActual && (
                            <span className="text-muted small">{new Date(tramite.fechaEstadoActual).toLocaleDateString('es-ES')}</span>
                        )}
                    </div>
                    <div className="d-flex gap-2 flex-wrap">
                        <button className="btn btn-sm btn-outline-secondary rounded-pill" onClick={() => setModalHistorialAbierto(true)}>
                            <i className="bi bi-clock-history me-1"></i>Ver historial
                        </button>
                        <BotonCancelarReactivarTramite tramite={tramite} onActualizado={recargarTramite} />
                    </div>
                </div>

                {tramite.observacionTribunal && !tramite.observacionTribunalResuelta && (
                    <div className="alert alert-warning border-0 rounded-3 d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                        <div><i className="bi bi-exclamation-triangle-fill me-2"></i><strong>Corrección pedida por el tribunal:</strong> {tramite.observacionTribunal}</div>
                        <button className="btn btn-sm btn-warning rounded-pill" onClick={marcarObservacionResuelta}>Marcar resuelta</button>
                    </div>
                )}

                {diasRestantes != null && (
                    <div className={`alert border-0 rounded-3 mb-3 alert-${colorPlazo}`}>
                        <i className="bi bi-hourglass-split me-2"></i>
                        Publicación enviada el {tramite.fechaPublicacionBoe}, vence el <strong>{tramite.fechaLimiteBoe}</strong>.{' '}
                        {diasRestantes >= 0
                            ? <>Faltan <strong>{diasRestantes}</strong> {diasRestantes === 1 ? 'día' : 'días'}.</>
                            : <>Ya se puede pedir la fijación de audiencia.</>}
                    </div>
                )}

                <SeccionAvanzarEstado idTramite={idTramite} nombreEstadoActual={tramite.nombreEstadoActual} onAvanzado={handleAvanzarEstado} />
            </div>

            <NotaComentario
                valor={tramite.comentarios || ''}
                onGuardar={handleGuardarComentario}
                label="Comentarios del Trámite de Declaratoria"
                color="blue"
                nombreUsuario={user?.nombre || ''}
            />

            {/* Cobranza del trámite: desplegable, debajo del estado y los comentarios */}
            <SeccionCobranzaTramite tramite={tramite} />

            {/* Causante(s) */}
            <div className="row g-4 mt-1">
                <div className="col-lg-6">
                    <div className="card shadow-lg border-0 rounded-4 p-4 h-100">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h4 className="fw-bold mb-0" style={{ color: '#9f1239' }}><i className="bi bi-person-x me-2"></i>Causante(s)</h4>
                            {!editMode && (
                                <button className="btn btn-sm rounded-pill px-3 fw-semibold" style={{ border: '1px solid #9f1239', color: '#9f1239' }} onClick={handleModificar}>
                                    <i className="bi bi-pencil me-1"></i>Editar
                                </button>
                            )}
                        </div>
                        {!editMode ? (
                            <>
                                <p><strong>Nombre:</strong> {tramite.causanteNombre || 'N/A'}</p>
                                <p><strong>DNI/CUIL:</strong> {tramite.causanteDniCuil || 'N/A'}</p>
                                <p><strong>Fecha de fallecimiento:</strong> {tramite.causanteFechaFallecimiento || 'N/A'}</p>
                                <p><strong>Padre:</strong> {tramite.causantePadre || 'N/A'} — <strong>Madre:</strong> {tramite.causanteMadre || 'N/A'}</p>
                                {tramite.causante2Nombre && (
                                    <>
                                        <hr />
                                        <p className="text-muted mb-1">Segundo causante</p>
                                        <p><strong>Nombre:</strong> {tramite.causante2Nombre}</p>
                                        <p><strong>DNI/CUIL:</strong> {tramite.causante2DniCuil || 'N/A'}</p>
                                        <p><strong>Fecha de fallecimiento:</strong> {tramite.causante2FechaFallecimiento || 'N/A'}</p>
                                    </>
                                )}
                                {tramite.etiqueta && <p className="mt-2"><strong>Etiqueta:</strong> {tramite.etiqueta}</p>}
                                <ChecklistTramite tramite={tramite} onActualizado={recargarTramite} />

                                {/* Pacto de honorarios: lo rellena solo el PACTO emitido en el
                                    alta (haberes y 30%) y se puede corregir acá hasta que nazca
                                    el cobro. */}
                                <TarjetaCobroTramite
                                    tramite={tramite}
                                    onPactoGenerado={() => Promise.all([recargarTramite(), recargarArchivos()])}
                                />
                            </>
                        ) : (
                            <>
                                {[
                                    ['causanteNombre', 'Nombre'], ['causanteDniCuil', 'DNI/CUIL'],
                                    ['causantePadre', 'Padre'], ['causanteMadre', 'Madre'],
                                ].map(([campo, label]) => (
                                    <div className="mb-2" key={campo}>
                                        <label className="form-label fw-semibold">{label}</label>
                                        <input type="text"
                                            className={`form-control ${campo === 'causanteDniCuil' && !esDniOCuilValido(tramiteEdit[campo]) ? 'is-invalid' : ''}`}
                                            value={tramiteEdit[campo]}
                                            onChange={(e) => setTramiteEdit(p => ({ ...p, [campo]: e.target.value }))} />
                                        {campo === 'causanteDniCuil' && !esDniOCuilValido(tramiteEdit[campo]) && (
                                            <div className="text-danger small">Debe ser un DNI (7 u 8 dígitos) o un CUIL/CUIT (11 dígitos).</div>
                                        )}
                                    </div>
                                ))}
                                <div className="mb-2">
                                    <label className="form-label fw-semibold">Fecha de fallecimiento</label>
                                    <input type="date" className="form-control" value={tramiteEdit.causanteFechaFallecimiento}
                                        onChange={(e) => setTramiteEdit(p => ({ ...p, causanteFechaFallecimiento: e.target.value }))} />
                                </div>
                                <div className="mb-2">
                                    <label className="form-label fw-semibold">Etiqueta</label>
                                    <input type="text" className="form-control" value={tramiteEdit.etiqueta}
                                        onChange={(e) => setTramiteEdit(p => ({ ...p, etiqueta: e.target.value }))} />
                                </div>
                                <div className="d-flex gap-2 mt-2">
                                    <button className="btn rounded-pill px-4 fw-semibold text-white" style={{ backgroundColor: '#9f1239' }} onClick={handleGuardarDatos} disabled={guardandoDatos}>
                                        {guardandoDatos ? <span className="spinner-border spinner-border-sm" role="status"></span> : <><i className="bi bi-check-circle me-2"></i>Guardar</>}
                                    </button>
                                    <button className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setEditMode(false)}>Cancelar</button>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                <div className="col-lg-6">
                    <div className="card shadow-lg border-0 rounded-4 p-4 h-100">
                        <h4 className="fw-bold mb-3" style={{ color: '#6c757d' }}>Información del Cliente</h4>
                        <p><strong>CUIL:</strong> {cliente.cuil || 'N/A'}</p>
                        <p><strong>Nombre:</strong> {cliente.nombre || 'N/A'}</p>
                        <p><strong>Apellido:</strong> {cliente.apellido || 'N/A'}</p>
                        <p><strong>Teléfono:</strong> {cliente.telefonoPropio || 'N/A'}</p>
                        {cliente.cuil && (
                            <div className="mt-3 d-flex justify-content-center">
                                <button className="btn btn-outline-secondary d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                                    onClick={() => verDetalleCliente(cliente.cuil)}>
                                    <PersonSearchIcon fontSize="medium" /> Ver Detalle del Cliente
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Herederos */}
            <div className="row g-4 mt-1">
                <div className="col-12">
                    <div className="card shadow-lg border-0 rounded-4 p-4">
                        <h4 className="fw-bold mb-3" style={{ color: '#9f1239' }}><i className="bi bi-people me-2"></i>Herederos</h4>
                        <TablaHerederos idTramite={idTramite} herederos={tramite.herederos} onActualizado={recargarTramite} />
                    </div>
                </div>
            </div>

            {/* Seguimiento judicial */}
            <div className="row g-4 mt-1">
                <div className="col-12">
                    <div className="card shadow-lg border-0 rounded-4 p-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h4 className="fw-bold mb-0" style={{ color: '#9f1239' }}><i className="bi bi-signpost-split me-2"></i>Seguimiento Judicial</h4>
                            {!editSeguimiento && (
                                <button className="btn btn-sm rounded-pill px-3 fw-semibold" style={{ border: '1px solid #9f1239', color: '#9f1239' }} onClick={handleModificarSeguimiento}>
                                    <i className="bi bi-pencil me-1"></i>Editar
                                </button>
                            )}
                        </div>
                        {!editSeguimiento ? (
                            <div className="row g-2">
                                <div className="col-md-6"><strong>Carátula judicial:</strong> {tramite.caratulaJudicial || 'N/A'}</div>
                                <div className="col-md-6"><strong>Expediente SAC:</strong> {tramite.nroExpedienteSac || 'N/A'}</div>
                                <div className="col-md-6"><strong>Juzgado:</strong> {tramite.juzgado || 'N/A'}</div>
                                <div className="col-md-6"><strong>Último escrito:</strong> {tramite.nroUltimoEscrito || 'N/A'} {tramite.fechaUltimoEscrito ? `(${tramite.fechaUltimoEscrito})` : ''}</div>
                                <hr />
                                <div className="col-md-4">{tramite.fechaInicioOps ? <><i className="bi bi-check-circle-fill text-success me-1"></i>OPS: {tramite.fechaInicioOps}</> : <span className="text-muted">OPS pendiente</span>}</div>
                                <div className="col-md-4">{tramite.fechaDecretoInicial ? <><i className="bi bi-check-circle-fill text-success me-1"></i>Decreto: {tramite.fechaDecretoInicial}</> : <span className="text-muted">Decreto pendiente</span>}</div>
                                <div className="col-md-4">{tramite.fechaPublicacionBoe ? <><i className="bi bi-check-circle-fill text-success me-1"></i>BOE: {tramite.fechaPublicacionBoe}</> : <span className="text-muted">BOE pendiente</span>}</div>
                                <div className="col-md-4">{tramite.fechaAudiencia ? <><i className="bi bi-check-circle-fill text-success me-1"></i>Audiencia: {tramite.fechaAudiencia} {tramite.horaAudiencia}</> : <span className="text-muted">Audiencia pendiente</span>}</div>
                                <div className="col-md-4">{tramite.fechaClausura ? <><i className="bi bi-check-circle-fill text-success me-1"></i>Clausura: {tramite.fechaClausura}</> : <span className="text-muted">Clausura pendiente</span>}</div>
                                <div className="col-md-4">{tramite.fechaSentencia ? <><i className="bi bi-check-circle-fill text-success me-1"></i>Sentencia: {tramite.fechaSentencia}</> : <span className="text-muted">Sentencia pendiente</span>}</div>
                                {tramite.motivoDesfavorable && <div className="col-12"><strong>Motivo desfavorable:</strong> {tramite.motivoDesfavorable}</div>}
                            </div>
                        ) : (
                            <>
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold">Carátula judicial</label>
                                        <input type="text" className="form-control" value={seguimientoEdit.caratulaJudicial}
                                            onChange={(e) => setSeguimientoEdit(p => ({ ...p, caratulaJudicial: e.target.value }))} />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold">Expediente SAC</label>
                                        <input type="text" className="form-control" value={seguimientoEdit.nroExpedienteSac}
                                            onChange={(e) => setSeguimientoEdit(p => ({ ...p, nroExpedienteSac: e.target.value }))} />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold">Juzgado</label>
                                        <input type="text" className="form-control" value={seguimientoEdit.juzgado}
                                            onChange={(e) => setSeguimientoEdit(p => ({ ...p, juzgado: e.target.value }))} />
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label fw-semibold">N° último escrito</label>
                                        <input type="text" className="form-control" value={seguimientoEdit.nroUltimoEscrito}
                                            onChange={(e) => setSeguimientoEdit(p => ({ ...p, nroUltimoEscrito: e.target.value }))} />
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label fw-semibold">Fecha último escrito</label>
                                        <input type="date" className="form-control" value={seguimientoEdit.fechaUltimoEscrito}
                                            onChange={(e) => setSeguimientoEdit(p => ({ ...p, fechaUltimoEscrito: e.target.value }))} />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label fw-semibold">Fecha publicación BOE</label>
                                        <input type="date" className="form-control" value={seguimientoEdit.fechaPublicacionBoe}
                                            onChange={(e) => setSeguimientoEdit(p => ({ ...p, fechaPublicacionBoe: e.target.value }))} />
                                        <small className="text-muted">Recalcula la fecha límite y mueve el evento de vencimiento.</small>
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label fw-semibold">Fecha audiencia</label>
                                        <input type="date" className="form-control" value={seguimientoEdit.fechaAudiencia}
                                            onChange={(e) => setSeguimientoEdit(p => ({ ...p, fechaAudiencia: e.target.value }))} />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label fw-semibold">Hora audiencia</label>
                                        <input type="time" className="form-control" value={seguimientoEdit.horaAudiencia}
                                            onChange={(e) => setSeguimientoEdit(p => ({ ...p, horaAudiencia: e.target.value }))} />
                                    </div>
                                    <div className="col-12">
                                        <label className="form-label fw-semibold">Observación del tribunal</label>
                                        <textarea className="form-control" rows={2} value={seguimientoEdit.observacionTribunal}
                                            onChange={(e) => setSeguimientoEdit(p => ({ ...p, observacionTribunal: e.target.value }))} />
                                    </div>
                                </div>
                                <div className="d-flex gap-2 mt-3">
                                    <button className="btn rounded-pill px-4 fw-semibold text-white" style={{ backgroundColor: '#9f1239' }} onClick={handleGuardarSeguimiento} disabled={guardandoSeguimiento}>
                                        {guardandoSeguimiento ? <span className="spinner-border spinner-border-sm" role="status"></span> : <><i className="bi bi-check-circle me-2"></i>Guardar</>}
                                    </button>
                                    <button className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setEditSeguimiento(false)}>Cancelar</button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Checklist de documentación */}
            <div className="row g-4 mt-1">
                <div className="col-12">
                    <div className="card shadow-lg border-0 rounded-4 p-4">
                        <h4 className="fw-bold mb-3" style={{ color: '#9f1239' }}><i className="bi bi-list-check me-2"></i>Checklist de documentación</h4>
                        {formularios.length === 0 ? (
                            <p className="text-muted fst-italic mb-0">No hay ítems de checklist.</p>
                        ) : (
                            <ul className="list-group list-group-flush">
                                {formularios.map(f => (
                                    <li key={f.id} className="list-group-item d-flex justify-content-between align-items-center flex-wrap gap-2 px-0">
                                        <div className="form-check">
                                            <input className="form-check-input" type="checkbox" id={`chk-${f.id}`}
                                                checked={!!f.presentado} disabled={!!f.idArchivo}
                                                onChange={() => togglePresentado(f)} />
                                            <label className="form-check-label" htmlFor={`chk-${f.id}`}
                                                style={{ textDecoration: f.presentado ? 'line-through' : 'none', color: f.presentado ? '#6c757d' : 'inherit' }}>
                                                {f.nombre}
                                            </label>
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            {f.idArchivo ? (
                                                <>
                                                    <button className="btn btn-sm btn-outline-primary rounded-pill"
                                                        onClick={() => descargarPorId(f.idArchivo)} title={nombrePorId[f.idArchivo] || 'Descargar'}>
                                                        <i className="bi bi-download me-1"></i>{nombrePorId[f.idArchivo] || 'Descargar'}
                                                    </button>
                                                    <button className="btn btn-sm btn-link text-danger p-0" title="Quitar adjunto" onClick={() => quitarAdjuntoChecklist(f)}>
                                                        <i className="bi bi-x-circle"></i>
                                                    </button>
                                                </>
                                            ) : (
                                                <label className="btn btn-sm btn-outline-secondary rounded-pill mb-0">
                                                    {subiendoItem === f.id ? <span className="spinner-border spinner-border-sm" role="status"></span> : <><i className="bi bi-paperclip me-1"></i>Adjuntar</>}
                                                    <input type="file" accept="application/pdf" hidden onChange={(e) => adjuntarArchivoChecklist(f, e.target.files[0])} />
                                                </label>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>

            {/* Eventos del Trámite */}
            <div className="row mb-3 mt-1">
                <div className="col-12">
                    <div className="card shadow-lg border-0 rounded-4 p-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h4 className="fw-bold mb-0" style={{ color: '#9f1239' }}>Eventos del Trámite</h4>
                            <button className="btn d-flex align-items-center gap-2 rounded-pill fw-semibold" style={{ border: '1px solid #9f1239', color: '#9f1239' }}
                                onClick={() => { const hoy = new Date(); setFechaSeleccionadaEvento(new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate())); setMostrarModalEvento(true); }}>
                                <span style={{ fontSize: '1.2rem' }}>+</span> Crear Evento
                            </button>
                        </div>
                        {cargandoEventos ? (
                            <div className="text-center py-4">
                                <div className="spinner-border" style={{ color: '#9f1239' }} role="status"></div>
                                <p className="mt-2 text-muted">Cargando eventos...</p>
                            </div>
                        ) : eventos.length === 0 ? (
                            <div className="alert alert-info border-0 rounded-3 text-center">
                                <p className="mb-0">No hay eventos registrados para este trámite</p>
                            </div>
                        ) : (
                            <div className="row g-3">
                                {eventos.map(evento => (
                                    <div key={evento.idEvento} className="col-md-6 col-lg-4">
                                        <div className="card h-100 border rounded-3 p-3 shadow-sm">
                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                <h6 className="fw-bold mb-0" style={{ color: '#9f1239' }}>{evento.titulo}</h6>
                                                {evento.tipoEvento && <span className="badge rounded-pill" style={{ backgroundColor: '#9f1239' }}>{evento.tipoEvento}</span>}
                                            </div>
                                            {evento.descripcion && <p className="text-muted small mb-2" style={{ whiteSpace: 'pre-line' }}>{evento.descripcion}</p>}
                                            <div className="mt-auto">
                                                <div className="d-flex align-items-center gap-2 mb-1">
                                                    <small className="text-muted">📅</small>
                                                    <small className="fw-semibold">{formatearFechaEvento(evento.fecha)}</small>
                                                </div>
                                                {evento.todoElDia ? (
                                                    <div className="d-flex align-items-center gap-2">
                                                        <small className="text-muted">🕐</small>
                                                        <small className="fw-semibold" style={{ color: '#9f1239' }}>Todo el día</small>
                                                    </div>
                                                ) : (
                                                    <div className="d-flex align-items-center gap-2">
                                                        <small className="text-muted">🕐</small>
                                                        <small>{evento.horaInicio} - {evento.horaFin}</small>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Otros documentos */}
            <div className="row g-4 mt-1">
                <div className="col-12">
                    <div className="card shadow-lg border-0 rounded-4 p-4">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h4 className="fw-bold mb-0" style={{ color: '#9f1239' }}><i className="bi bi-paperclip me-2"></i>Otros documentos</h4>
                            <label className="btn btn-sm rounded-pill mb-0" style={{ border: '1px solid #9f1239', color: '#9f1239' }}>
                                {subiendoGeneral ? <span className="spinner-border spinner-border-sm" role="status"></span> : <><i className="bi bi-cloud-upload me-1"></i>Subir archivo</>}
                                <input type="file" hidden onChange={(e) => { subirGeneral(e.target.files[0]); e.target.value = ''; }} />
                            </label>
                        </div>
                        {archivosOtros.length === 0 ? (
                            <p className="text-muted fst-italic mb-0">No hay documentos cargados.</p>
                        ) : (
                            <div className="d-flex flex-column gap-2">
                                {archivosOtros.map(a => (
                                    <div key={a.idArchivo} className="d-flex align-items-center justify-content-between border rounded-3 px-3 py-2">
                                        <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                                            <i className="bi bi-file-earmark-text" style={{ fontSize: '1.25rem', color: '#9f1239' }}></i>
                                            <div style={{ minWidth: 0 }}>
                                                <div className="fw-semibold text-truncate" style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                                    onClick={() => descargarPorId(a.idArchivo)} title={a.nombre}>
                                                    {a.nombre}
                                                </div>
                                                <small className="text-muted">{formatTamano(a.tamaño)}</small>
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center gap-1 flex-shrink-0">
                                            <button className="btn btn-sm" style={{ color: '#0056b3', border: 'none' }} title="Descargar" onClick={() => descargarPorId(a.idArchivo)}>
                                                <i className="bi bi-download"></i>
                                            </button>
                                            <button className="btn btn-sm" style={{ color: '#dc3545', border: 'none' }} title="Eliminar" onClick={() => eliminarGeneral(a.idArchivo)}>
                                                <i className="bi bi-trash"></i>
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <CrearEventoModal
                isOpen={mostrarModalEvento}
                onClose={() => setMostrarModalEvento(false)}
                onSave={handleGuardarEvento}
                selectedDate={fechaSeleccionadaEvento}
                idTramite={idTramite}
            />

            <PanelAccionesAvanzadasTramite idTramite={tramite?.idTramite} tipoTramite="DECLARATORIA_HEREDEROS" />

            <HistorialTramite
                isOpen={modalHistorialAbierto}
                onClose={() => setModalHistorialAbierto(false)}
                idTramite={idTramite}
                onEstadoCambiado={recargarTramite}
                ambitoFiltro="DECLARATORIA_HEREDEROS"
            />

            <CustomModal {...modalState} onHide={hideModal} />
            <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} newestOnTop closeOnClick pauseOnHover />
            <FeedbackButton tramite={tramite} />
        </div>
    );
}

export default VistaDetalladaTramiteDeclaratoria;
